<template>
  <ClientOnly>
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="trapRef"
        class="modal__backdrop"
        tabindex="0"
        @mousedown.self="closeModal"
      >
        <div
          :class="[
            'modal',
            {
              'modal--centered': isCentered,
            },
          ]"
          role="dialog"
          aria-modal="true"
          :style="maxWidthStyle"
        >
          <nav class="modal__nav">
            <button
              aria-label="Close modal"
              :class="[
                'modal__close-button',
                { 'modal__close-button--blue': displayHeadSection },
              ]"
              @click="closeModal"
            />
          </nav>
          <div v-if="displayHeadSection" class="modal__head">
            <slot name="head"></slot>
          </div>
          <div
            :class="[
              'modal__body',
              { 'modal__body--small-padding': smallPadding },
            ]"
            @click="handleLinkClick"
          >
            <slot name="body"></slot>
          </div>
          <div v-if="!hideFooter" class="modal__footer">
            <BaseButton
              outlined
              :title="$t('common.close_modal')"
              class="modal__bottom-button"
              aria-label="Close modal"
              @click="closeModal"
            >
              <template #text> {{ $t('common.close_modal') }} </template>
            </BaseButton>
          </div>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
interface Props {
  smallPadding?: boolean
  hideFooter?: boolean
  maxWidth?: string
  isCentered?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  smallPadding: false,
  hideFooter: false,
  maxWidth: '1000',
  isCentered: false,
})
const { blockScroll } = useScrollBlock({ blockUntilMounted: false })

const isOpen = ref(false)
const { trapRef } = useFocusTrap()

const slots = useSlots()

const displayHeadSection = computed(() => {
  return slots.head && slots.head().length > 0
})

const openModal = () => {
  isOpen.value = true
  blockScroll()
}

const closeModal = () => {
  isOpen.value = false
  blockScroll(false)
}
const maxWidthStyle = computed(() => {
  return {
    '--max-width-desktop': `${props.maxWidth || 1000}px`,
  }
})

const handleKeypress = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeModal()
  }
}

const handleLinkClick = (event: MouseEvent) => {
  const targetTag = (event.target as HTMLElement).tagName.toLowerCase()
  const targetParentTag = (
    (event.target as HTMLElement).parentElement as HTMLElement
  )?.tagName.toLowerCase()

  const isLinkClicked = targetTag === 'a' || targetParentTag === 'a'

  if (isLinkClicked) {
    closeModal()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeypress)
})

onUnmounted(() => {
  blockScroll(false)
  window.removeEventListener('keydown', handleKeypress)
})

defineExpose({
  openModal,
  closeModal,
})
</script>

<style lang="scss" scoped>
@keyframes backdrop {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modal {
  from {
    transform: translateY(60px);
  }

  to {
    transform: translateY(0);
  }
}
.modal {
  --max-width-desktop: 1000px;
  --max-width-mobile: 100%;

  $self: &;

  margin: 0 auto;
  background-color: white;
  width: var(--max-width-mobile);
  position: absolute;
  animation: modal 0.3s ease-in-out;

  @media (min-width: $breakpoint-lg) {
    max-width: var(--max-width-desktop);
    border-radius: 24px;
    left: 0;
    right: 0;
    align-self: center;
  }
  &--centered {
    align-self: center;
    @media (min-width: $breakpoint-md) {
      border-radius: 24px;
      min-height: auto;
      max-width: var(--max-width-desktop);
    }

    #{$self}__body {
      @media (min-width: $breakpoint-md) {
        min-height: auto;
      }
    }
  }

  &__backdrop {
    position: fixed;
    top: 0;
    display: flex;
    left: 0;
    z-index: 10000;
    width: 100vw;
    height: 100vh;
    background-color: rgba(17, 41, 80, 0.3);
    overflow-y: scroll;
    animation: backdrop 0.3s ease-in-out;
  }

  &__content {
    overflow-y: scroll;
  }

  &__nav {
    position: absolute;
    top: 0;
    right: 0;
    width: fit-content;
    height: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 24px 20px 0 0;

    @media (min-width: $breakpoint-md) {
      padding: 32px 48px 0 0;
    }
  }

  &__head {
    background-color: #f5f9ff;
    padding: 116px 20px 80px 20px;

    @media (min-width: $breakpoint-md) {
      padding: 132px 80px 80px 80px;
    }

    @media (min-width: $breakpoint-lg) {
      padding: 132px 128px 80px 128px;
      border-radius: 24px 24px 0 0;
    }
  }

  &__body {
    padding: 80px 20px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;

    @media (min-width: $breakpoint-md) {
      padding: 80px;
    }

    @media (min-width: $breakpoint-lg) {
      padding: 80px 128px;
      min-height: auto;
    }
    &--small-padding {
      @media (min-width: $breakpoint-lg) {
        padding: 104px 64px 64px 64px;
      }
    }
  }

  &__close-button {
    position: sticky;
    top: 24px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-image: url('@/assets/icons/fullview-modal-close.svg');
    background-repeat: no-repeat;
    background-size: 48px;
    background-position: center;
    cursor: pointer;
    z-index: 10;
    content: url('@/assets/icons/fullview-modal-close.svg')
      url('@/assets/icons/close-cobalt.svg');

    @media (min-width: $breakpoint-md) {
      top: 32px;
    }

    &:hover,
    &:focus {
      background-image: url('@/assets/icons/close-cobalt.svg');
      background-size: 16px;
    }

    &:focus {
      outline: 2px solid var(--cobalt-600);
    }
    &--blue {
      background-color: #f5f9ff;
    }
  }

  &__footer {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 20px 24px;

    @media (min-width: $breakpoint-md) {
      padding: 0 80px 40px;
    }

    @media (min-width: $breakpoint-lg) {
      padding: 0 128px 40px;
    }
  }
}
</style>
